import { characterData } from './characterData.js';
import { ModelManager } from './ModelManager.js';

export class CharacterSelector {
    constructor() {
        this.modalElement = document.getElementById('switchConfirmModal');
        this.modal = new bootstrap.Modal(this.modalElement);
        this.selectedModelId = null;
        this.selectedCharacterName = null;
        this.characterInfoBtn = document.getElementById('character-info-btn');
        this.characterButtonImage = document.querySelector('.character-button-image');
        this.characterInfoSection = document.getElementById('character-info-section');
        this.chatHistory = document.getElementById('chat-history');

        // Get character selection UI element
        this.characterSelectionUI = document.getElementById('character-selection-ui');
        
        // Initialize ModelManager and make it globally accessible
        this.modelManager = new ModelManager();
        window.modelManager = this.modelManager; // Expose to global scope (lowercase)
        window.ModelManager = this.modelManager; // Also expose with uppercase for compatibility
        
        // Get the current selected model from ModelManager
        const currentModelId = this.modelManager.getCurrentModelId();
        
        // Update the UI with the centrally stored model
        const characterModel = document.getElementById('character-model');
        if (characterModel) {
            if (currentModelId) {
                characterModel.value = currentModelId;
                console.log('CharacterSelector: Using model ID from ModelManager:', currentModelId);
            }
            this.updateCharacterInfo(characterModel.value);
        }

        this.bindEvents();
        this.initializeModelLoadingListener();
        this.showCharacterSelection();
        this.bindMenuEvents();
    }

    showPanel(panelElement) {
        // Show the character selection panel
        if (panelElement) {
            panelElement.classList.add('active');
        }
    }

    showCharacterSelection() {
        this.showPanel(this.characterSelectionUI);
    }

    bindEvents() {
        // Character info button click event
        if (this.characterInfoBtn) {
            this.characterInfoBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const infoSection = document.getElementById('character-info-section');
                if (infoSection) {
                    infoSection.classList.toggle('d-none');
                    this.chatHistory.classList.toggle('with-info');
                    window.dispatchEvent(new Event('resize'));
                }
            });
        }

        // Confirm button click event
        const confirmButton = document.getElementById('confirm-switch');
        if (confirmButton) {
            confirmButton.addEventListener('click', async () => {
                const modelId = confirmButton.dataset.modelId;
                if (modelId) {
                    try {
                        // Use the class instance of ModelManager
                        
                        // Call setCurrentModel which will update both server and localStorage and reload
                        const success = await this.modelManager.setCurrentModel(modelId, true);
                        
                        if (!success) {
                            console.error('ModelManager: Failed to set current model');
                        }
                    } catch (error) {
                        console.error('Error switching character:', error);
                    }
                }
            });
        }
    }

    initializeModelLoadingListener() {
        document.addEventListener('modelLoadingComplete', (event) => {
            console.log('Model loading complete, updating character info');
            const characterModel = document.getElementById('character-model');
            if (characterModel) {
                this.updateCharacterInfo(characterModel.value);
            }
            
            // Save character name to localStorage for cross-page access
            const modelId = this.modelManager?.getCurrentModelId() || characterModel?.value;
            if (modelId && characterData[modelId]) {
                localStorage.setItem('selectedCharacterName', characterData[modelId].name);
                console.log('CharacterSelector: Saved selectedCharacterName on model load:', characterData[modelId].name);
            }
        });

        document.addEventListener('characterSwitch', (event) => {
            const characterModel = document.getElementById('character-model');
            if (characterModel) {
                this.updateCharacterInfo(characterModel.value);
            }
        });
        
        // Wait for DOM to be fully loaded and then dispatch an initial character-changed event
        // This helps any components that need character data on page load
        setTimeout(() => {
            const modelId = this.modelManager.getCurrentModelId();
            if (modelId) {
                const info = characterData[modelId];
                if (info) {
                    // Save character name to localStorage for cross-page access (e.g., tutor page)
                    localStorage.setItem('selectedCharacterName', info.name);
                    console.log('CharacterSelector: Saved selectedCharacterName on initial load:', info.name);
                    
                    // Dispatch a character-changed event with initial model data
                    const characterChangedEvent = new CustomEvent('character-changed', {
                        detail: {
                            modelId: modelId,
                            name: info.name,
                            imageUrl: info.imageUrl
                        },
                        bubbles: true,
                        cancelable: true
                    });
                    document.dispatchEvent(characterChangedEvent);
                    console.log('Dispatched initial character-changed event for:', info.name);
                }
            }
        }, 1000); // Short delay to ensure other components are loaded
    }

    updateCharacterInfo(modelId) {
        // Handle null or undefined modelId
        if (!modelId) {
            console.log('No model ID provided, updating UI for no model state');
            // Update the chat header for no model state
            this.updateChatHeader(null);
            
            // Set a placeholder image instead of clearing it
            if (this.characterButtonImage) {
                this.characterButtonImage.src = '/static/images/logo.png';
                this.characterButtonImage.alt = 'Select a Character';
            }
            
            // Clear other UI elements
            const elements = {
                'character-modal-image': {prop: 'src', value: ''},
                'character-name': {prop: 'textContent', value: 'No Character Selected'},
                'character-origin': {prop: 'textContent', value: ''},
                'character-expertise': {prop: 'textContent', value: ''},
                'character-description': {prop: 'textContent', value: 'Please select a character to begin.'}
            };
            
            for (const [id, data] of Object.entries(elements)) {
                const element = document.getElementById(id);
                if (element) {
                    element[data.prop] = data.value;
                }
            }
            
            return;
        }
        
        const info = characterData[modelId];
        if (!info) {
            console.error('No character info found for model ID:', modelId);
            this.updateChatHeader(null);
            return;
        }

        // Update character info
        if (this.characterButtonImage) {
            this.characterButtonImage.src = info.imageUrl;
            this.characterButtonImage.alt = info.name;
        }

        const elements = {
            'character-modal-image': {prop: 'src', value: info.imageUrl},
            'character-name': {prop: 'textContent', value: info.name},
            'character-origin': {prop: 'textContent', value: info.origin},
            'character-expertise': {prop: 'textContent', value: info.expertise},
            'character-description': {prop: 'textContent', value: info.description}
        };

        for (const [id, data] of Object.entries(elements)) {
            const element = document.getElementById(id);
            if (element) {
                element[data.prop] = data.value;
            }
        }

        this.updateChatHeader(info.name);
        
        // Dispatch a character-changed event for other components to listen to
        const characterChangedEvent = new CustomEvent('character-changed', {
            detail: {
                modelId: modelId,
                name: info.name,
                imageUrl: info.imageUrl
            },
            bubbles: true,
            cancelable: true
        });
        document.dispatchEvent(characterChangedEvent);
        
        // Save selected character name to localStorage for cross-page access (e.g., tutor page)
        localStorage.setItem('selectedCharacterName', info.name);
        console.log('CharacterSelector: Saved selectedCharacterName to localStorage:', info.name);
        
        // Dispatch event with character name for other components (like ImageGalleryController)
        document.dispatchEvent(new CustomEvent('characterSelected', {
            detail: {
                id: modelId,
                name: info.name
            }
        }));
    }

    showConfirmation(modelId, characterName) {
        this.selectedModelId = modelId;
        this.selectedCharacterName = characterName;

        const selectedCharacterNameElement = document.getElementById('selected-character-name');
        if (selectedCharacterNameElement) {
            selectedCharacterNameElement.textContent = characterName;
        }

        this.modal.show();
    }

    updateChatHeader(characterName) {
    }

    bindMenuEvents() {
        // Add menu button event listeners
        const switchCharacterBtn = document.getElementById('switch-character-menu-btn');
        const museumBtn = document.getElementById('museum-menu-btn');
        const characterPanel = document.getElementById('character-panel');
        const imageGalleryPanel = document.getElementById('image-gallery-panel');

        if (switchCharacterBtn) {
            switchCharacterBtn.addEventListener('click', () => {
                if (characterPanel) {
                    // Show and fully expand character panel
                    characterPanel.style.transform = 'translateY(0)';
                    characterPanel.style.display = 'block';
                    
                    // Set the isPanelExpanded state to true if it exists
                    if (window.isPanelExpanded !== undefined) {
                        window.isPanelExpanded = true;
                    }
                }

                // Hide image panel if it exists
                if (imageGalleryPanel) {
                    imageGalleryPanel.style.display = 'none';
                }
            });
        }

        if (museumBtn) {
            museumBtn.addEventListener('click', () => {
                // Hide character panel
                if (characterPanel) {
                    characterPanel.style.transform = '';
                    characterPanel.style.display = 'none';
                }

                // Show image panel and trigger museum open event
                if (imageGalleryPanel) {
                    imageGalleryPanel.style.display = 'block';
                    
                    // Apply maxHeight constraint if not already set
                    const maxHeight = window.innerHeight * 0.85;
                    imageGalleryPanel.style.maxHeight = `${maxHeight}px`;
                }
                document.dispatchEvent(new CustomEvent('openMuseum'));
            });
        }
    }
}
